// Guided setup inputs and navigation, plus backward reads of historical cards.
// New workspace proposals live in lib/controlProposalSession.js. Guided setup
// stages answers here and applies compiled settings only through Add to draft.
// No wizard context or answer tools are installed on the workspace chat agent.
import { getAuthIdentityEpoch } from "../../../lib/authTokenStore.js";
import { openAgentPolicyPilot } from "../../../lib/agentPolicyNavigation.js";
import { patchChatJob, postChatJob } from "../../../lib/chatJobs.js";
import { setValueAt } from "../catalogDocument.js";
import { AGENT_IDS, AGENT_LABELS, CHANGES, DECISION_LABELS, RULE_KINDS, changeById, supportedAgents } from "./changes.js";
import { SOURCES, readServerEndpoints } from "./sources.js";

export const POLICY_CHAT_JOB = "policyChat";
const VIEW_CAP = 20;
const SAVE_DELAY_MS = 400;
export const ENDED_TEXT = "This proposal is historical. Continue its local draft from the investigation, or use Configure controls to prepare a new one.";
// The parts of a session that the chat saves; the rest are functions.
const SAVED = ["id", "version", "canManage", "today", "changeId", "on", "answers", "done", "cur", "notes", "views", "presented", "rows", "rowsState", "pilot", "started", "activeAgent", "pilotError", "serverEndpoints", "context", "conversational"];

const sessions = new Map();
const listeners = new Map();
let seq = 0;

function emit(id) {
    for (const fn of listeners.get(id) ?? []) fn();
}

export function subscribeSession(id, fn) {
    if (!listeners.has(id)) listeners.set(id, new Set());
    listeners.get(id).add(fn);
    return () => listeners.get(id)?.delete(fn);
}

export const getSession = (id) => sessions.get(id) ?? null;

const saveTimers = new Map();
const pendingEndpoints = new Map();

function scheduleSave(id) {
    if (saveTimers.has(id)) return;
    saveTimers.set(id, setTimeout(() => {
        saveTimers.delete(id);
        const session = sessions.get(id);
        if (session && !session.ended) Promise.resolve(session.saveSnapshot(id, snapshotOf(session))).catch(() => {});
    }, SAVE_DELAY_MS));
}

/// The saved form of a session: plain data only.
export function snapshotOf(session) {
    return Object.fromEntries(SAVED.map((k) => [k, session[k]]));
}

function update(id, patch) {
    const current = sessions.get(id);
    if (!current || current.ended || (current.guard && !current.guard())) return null;
    const next = { ...current, ...(typeof patch === "function" ? patch(current) : patch), version: current.version + 1 };
    sessions.set(id, next);
    emit(id);
    next.onChange?.(next);
    scheduleSave(id);
    return next;
}

/// Puts a saved session back, ended, unless this page already holds it. A
/// read that was running when the copy was saved counts as failed.
export function restoreSession(saved) {
    if (!saved?.id || sessions.has(saved.id)) return;
    const rowsState = Object.fromEntries(Object.entries(saved.rowsState ?? {}).map(([k, v]) => [k, v === "loading" ? "error" : v]));
    const serverEndpoints = Object.fromEntries(Object.entries(saved.serverEndpoints ?? {}).map(([k, v]) => [k, v?.state === "loading" ? { state: "error", rows: [] } : v]));
    sessions.set(saved.id, {
        ...saved,
        rowsState,
        serverEndpoints,
        ended: true,
        readSource: async () => { throw new Error("This policy chat has ended."); },
        readEndpoints: async () => { throw new Error("This policy chat has ended."); },
        startPilot: null,
        postCard: () => {},
        saveSnapshot: () => {},
    });
    queueMicrotask(() => emit(saved.id));
}

/// Restores every Policy chat session saved on a chat's job rows, as a chat
/// loads.
export function restoreSessionsFrom(items) {
    for (const it of items || []) {
        if (it?.kind === "job" && it.job === POLICY_CHAT_JOB && it.policySession) restoreSession(it.policySession);
    }
}

const saveOnPicker = (id, snapshot) => patchChatJob(`${id}|pick`, { policySession: snapshot });

/// The card a job key names: `${sessionId}|${card}`.
export function parseCardKey(key) {
    const [sessionId, ...rest] = String(key || "").split("|");
    return { sessionId, card: rest.join("|") };
}

function post(id, card, post = postChatJob, { reveal = false } = {}) {
    const session = getSession(id);
    if (!session) return;
    if (session.conversational && session.guard && !session.guard()) return false;
    if (session.conversational && !card.startsWith("view:")
        && ((!proposalReviewAvailable(session) && !session.reviewOpen) || session.isChatBusy?.())) return false;
    return post({ kind: POLICY_CHAT_JOB, key: `${id}|${session.compact && !card.startsWith("view:") ? "active" : card}`,
        ...(reveal ? { reveal: true } : {}),
        ...(card.startsWith("view:") ? { policyView: session.views[Number(card.slice(5))] } : {}) });
}

/**
 * Starts a session. `today` holds each agent's current signed policy as
 * `{ document, generation, openPilot, unsavedEdits }`; `readSource` reads a
 * data source by id; `startPilot(agentId, { document, expectedGeneration,
 * endpointIds })` starts one pilot. `postCard` defaults to the chat console.
 */
export function createSession({ today, canManage, context = null, readSource = (sourceId) => SOURCES[sourceId].read(), readEndpoints = readServerEndpoints, startPilot, postCard = postChatJob, saveSnapshot = saveOnPicker, id: suppliedId = null, compact = false, conversational = false, isChatBusy = null, guard = null, onChange = null }) {
    seq += 1;
    const id = suppliedId || `pc${Date.now().toString(36)}${seq}`;
    sessions.set(id, {
        id,
        version: 0,
        canManage: !!canManage,
        today,
        context,
        changeId: null,
        on: {},
        answers: {},
        done: [],
        cur: "pick",
        notes: [],
        views: [],
        presented: {},
        rows: {},
        rowsState: {},
        serverEndpoints: {},
        pilot: {},
        started: null,
        activeAgent: null,
        pilotError: null,
        readSource,
        readEndpoints,
        startPilot,
        postCard,
        saveSnapshot,
        runtimeToken: {},
        compact,
        conversational,
        isChatBusy,
        guard,
        onChange,
    });
    return id;
}

/// Posts the change picker, the session's first card.
export function postPicker(id) {
    const session = getSession(id);
    if (session) post(id, "pick", session.postCard);
}

export function endSession(id) {
    clearTimeout(saveTimers.get(id));
    saveTimers.delete(id);
    for (const key of pendingEndpoints.keys()) if (key.startsWith(`${id}|`)) pendingEndpoints.delete(key);
    sessions.delete(id);
    emit(id);
}

export async function flushSessionSave(id) {
    clearTimeout(saveTimers.get(id));
    saveTimers.delete(id);
    const session = getSession(id);
    if (session && !session.ended) await session.saveSnapshot(id, snapshotOf(session));
}

export function restoreSubmittedProposal(id, results, activeAgent = null) {
    const session = getSession(id);
    if (!session) return;
    const restored = (results || []).map((result) => ({ ...result, ...(result.unknown ? { recoveryRequired: true } : {}) }));
    if (activeAgent && !restored.some((result) => result.agentId === activeAgent)) restored.push({ agentId: activeAgent, ok: false, unknown: true, recoveryRequired: true,
        error: "The launch was interrupted. Recover its saved request in Investigation before trying again." });
    sessions.set(id, { ...session, version: session.version + 1, started: restored.length ? restored : null, activeAgent: null, cur: "review", reviewOpen: true });
    emit(id);
}

export function requireProposalReview(id, message) {
    const session = getSession(id);
    if (session) update(id, { needsReview: message, ...(planSteps(session).every((step) => session.done.includes(step.id)) ? { cur: "review" } : {}) });
}

export const proposalReviewAvailable = (session) => !!session && (["review", "pilot", "starting", "done"].includes(session.cur)
    || (!!session.changeId && !missingProposalSteps(session).length));

export function setConversationalProposal(id) {
    if (!getSession(id)?.conversational) update(id, { conversational: true });
}

export function showCurrentProposal(id, { review = false, reveal = false } = {}) {
    if (review || reveal) update(id, { ...(review ? { reviewOpen: true } : {}), ...(reveal ? { revealRequested: true } : {}) });
    const session = getSession(id);
    if (!session) return false;
    const posted = post(id, session.cur === "pick" ? "pick" : session.cur, session.postCard, { reveal: reveal || !!session.revealRequested });
    if (posted && session.revealRequested) update(id, { revealRequested: false });
    return posted;
}

// ---------------------------------------------------------------- plan

export const changeOf = (session) => (session?.changeId ? changeById(session.changeId) : null);

export function inScope(session, agentId) {
    const change = changeOf(session);
    return !!change && (!session.context || !!session.today[agentId]) && change.support[agentId].level !== "no" && session.on[agentId] !== false;
}

/// Every step in order. The agents step comes first when more than one agent
/// is supported; agent-only steps come after the general ones. A step with
/// `when` is in the plan only while the answers so far meet it.
export function planSteps(session) {
    const change = changeOf(session);
    if (!change) return [];
    const lead = supportedAgents(change).length > 1 ? [AGENTS_STEP] : [];
    const general = change.steps.filter((s) => s.scope === "all" && (!s.when || s.when(session.answers ?? {})));
    const perAgent = AGENT_IDS.filter((a) => inScope(session, a)).flatMap((a) => change.steps.filter((s) => s.scope === a));
    return [...lead, ...general, ...perAgent];
}

export const AGENTS_STEP = Object.freeze({
    id: "agents",
    card: "agents",
    scope: "all",
    question: "Which agents should this change cover?",
    model: { mayInfer: "Agents from their names.", mustConfirm: null, outOfScope: {} },
    examples: [{ say: "just Claude Code", answer: ["claude-code"] }],
});

export function stepById(session, stepId) {
    if (stepId === "agents") return AGENTS_STEP;
    return changeOf(session)?.steps.find((s) => s.id === stepId) ?? null;
}

const todayDocuments = (session) => Object.fromEntries(AGENT_IDS.map((a) => [a, session.today?.[a]?.document ?? {}]));

/// The rows a step's card shows.
export function rowsFor(session, step) {
    if (step.source) return session.rows[step.source] ?? [];
    if (typeof step.rows === "function") return step.rows({ today: todayDocuments(session) });
    return step.rows ?? [];
}

/// Changes for one agent: `[{ pointer, value }]`, `value` undefined to remove.
export function changesFor(session, agentId) {
    const change = changeOf(session);
    if (!change || !inScope(session, agentId)) return [];
    const rows = Object.values(session.rows).flat();
    return change.writes(session.answers, agentId, session.today?.[agentId]?.document ?? {}, rows);
}

export const changedAgents = (session) => AGENT_IDS.filter((a) => changesFor(session, a).length);

export const missingProposalSteps = (session) => planSteps(session).filter((step) => !session.done.includes(step.id)
    || (step.source && session.rowsState[step.source] !== "ready"));
export const pilotResultFor = (session, agentId) => session.started?.find((result) => result.agentId === agentId);
export const pilotAgentLocked = (session, agentId) => !!(pilotResultFor(session, agentId)?.ok || pilotResultFor(session, agentId)?.recoveryRequired || session.today[agentId]?.openPilot);
export const remainingPilotAgents = (session) => changedAgents(session).filter((agentId) => !pilotAgentLocked(session, agentId));

/// The document a pilot would carry for one agent.
export function documentFor(session, agentId) {
    return changesFor(session, agentId).reduce((doc, { pointer, value }) => setValueAt(doc, pointer, value), session.today?.[agentId]?.document ?? {});
}

export function warnings(session) {
    const change = changeOf(session);
    if (!change) return [];
    const rows = Object.values(session.rows).flat();
    return change.impact(session.answers, rows, { today: todayDocuments(session), inScope: AGENT_IDS.filter((a) => inScope(session, a)) });
}

// ---------------------------------------------------------------- moving through the plan

async function loadSource(id, sourceId) {
    const session = getSession(id);
    if (!session || session.rowsState[sourceId]) return;
    update(id, (s) => ({ rowsState: { ...s.rowsState, [sourceId]: "loading" } }));
    try {
        const rows = await session.readSource(sourceId);
        if (getSession(id)?.runtimeToken !== session.runtimeToken) return;
        update(id, (s) => {
            const step = planSteps(s).find((x) => x.source === sourceId);
            // Today's policy seeds the step; anything already answered wins.
            const seeded = step?.start ? { [step.id]: { ...step.start({ today: todayDocuments(s), rows }), ...(s.answers[step.id] ?? {}) } } : {};
            return { rows: { ...s.rows, [sourceId]: rows }, rowsState: { ...s.rowsState, [sourceId]: "ready" }, answers: { ...s.answers, ...seeded } };
        });
    } catch {
        if (getSession(id)?.runtimeToken !== session.runtimeToken) return;
        update(id, (s) => ({ rowsState: { ...s.rowsState, [sourceId]: "error" } }));
    }
}

/// Reads the endpoints that called one MCP server, once per session, and
/// returns them: `{ state: "loading" | "ready" | "error", rows }`.
export function loadServerEndpoints(id, serverId) {
    const session = getSession(id);
    if (!session || !serverId) return Promise.resolve({ state: "error", rows: [] });
    const known = session.serverEndpoints?.[serverId];
    if (known?.state === "ready" || session.ended) return Promise.resolve(known ?? { state: "error", rows: [] });
    const key = `${id}|${serverId}`;
    if (pendingEndpoints.has(key)) return pendingEndpoints.get(key);
    update(id, (s) => ({ serverEndpoints: { ...s.serverEndpoints, [serverId]: { state: "loading", rows: [] } } }));
    const pending = session.readEndpoints(serverId)
        .then((rows) => ({ state: "ready", rows }))
        .catch(() => ({ state: "error", rows: [] }))
        .then((result) => {
            if (pendingEndpoints.get(key) === pending) pendingEndpoints.delete(key);
            if (getSession(id)?.runtimeToken !== session.runtimeToken) return { state: "error", rows: [] };
            update(id, (s) => ({ serverEndpoints: { ...s.serverEndpoints, [serverId]: result } }));
            return result;
        });
    pendingEndpoints.set(key, pending);
    return pending;
}

function present(id, stepId) {
    // A step shown again gets a new card; only its latest card takes answers.
    const session = update(id, (s) => ({ cur: stepId, presented: { ...s.presented, [stepId]: (s.presented?.[stepId] ?? 0) + 1 } }));
    if (!session) return;
    const step = stepById(session, stepId);
    if (step?.source) loadSource(id, step.source);
    post(id, `step:${stepId}:${session.presented[stepId]}`, session.postCard);
}

function advance(id) {
    const session = getSession(id);
    const next = planSteps(session).find((s) => !session.done.includes(s.id));
    if (next) { present(id, next.id); return; }
    update(id, { cur: "review" });
    post(id, "review", session.postCard);
}

export function chooseChange(id, changeId) {
    const change = changeById(changeId);
    const session = getSession(id);
    if (!change || !session || session.ended || session.changeId || (session.guard && !session.guard())) return false;
    const today = todayDocuments(session);
    const answers = {};
    for (const step of change.steps) {
        if (step.start && !step.source) answers[step.id] = step.start({ today });
        else if (step.card === "decisions" || step.card === "themes") answers[step.id] = {};
        else if (step.card === "rules") answers[step.id] = [];
        else if (step.card === "features") answers[step.id] = {};
    }
    update(id, {
        changeId,
        answers,
        on: Object.fromEntries(AGENT_IDS.map((a) => [a, change.support[a].level !== "no" && (!session.context?.observedAgents?.length || session.context.observedAgents.includes(a))])),
        pilot: Object.fromEntries(AGENT_IDS.map((a) => [a, { on: true, endpoints: [] }])),
    });
    advance(id);
    return true;
}

/// Sets part of an answer without confirming the step (a click on a card).
export function draftAnswer(id, stepId, value) {
    const session = getSession(id);
    if (!session || session.ended || session.cur === "starting" || session.started) return;
    update(id, (s) => (stepId === "agents" ? { on: { ...s.on, ...value } } : { answers: { ...s.answers, [stepId]: value } }));
}

/// Confirms a step; moves on when it was the current step.
export function confirmStep(id, stepId) {
    const session = getSession(id);
    if (!session || session.ended) return;
    if (stepById(session, stepId)?.card === "text" && !String(session.answers[stepId] || "").trim()) return;
    const wasCurrent = session.cur === stepId;
    update(id, (s) => ({ done: s.done.includes(stepId) ? s.done : [...s.done, stepId] }));
    if (wasCurrent) advance(id);
}

export function reopenStep(id, stepId) {
    const session = getSession(id);
    if (!session || session.ended || session.cur === "starting" || !stepById(session, stepId) || session.started) return false;
    update(id, (s) => ({ done: s.done.filter((d) => d !== stepId) }));
    present(id, stepId);
    return true;
}

export function addNote(id, stepId, text) {
    const note = String(text).trim().slice(0, 300);
    update(id, (s) => (s.notes.some((n) => n.text === note) ? {} : { notes: [...s.notes, { step: stepId, text: note }] }));
}

export function showView(id, view) {
    const session = getSession(id);
    const n = session?.views.length ?? 0;
    if (!session || session.ended || (session.guard && !session.guard()) || n >= VIEW_CAP) return null;
    update(id, (s) => ({ views: [...s.views, view] }));
    post(id, `view:${n}`, session.postCard);
    return n;
}

export function toPilot(id) {
    const session = getSession(id);
    if (!session || session.ended || session.cur === "starting" || !session.changeId || (!session.reviewOpen && !proposalReviewAvailable(session)) || !remainingPilotAgents(session).length) return;
    update(id, { cur: "pilot", reviewOpen: false, needsReview: null });
    post(id, "pilot", session.postCard);
}

export function setPilot(id, agentId, patch) {
    const session = getSession(id);
    if (!session || session.ended || session.cur === "starting" || pilotAgentLocked(session, agentId)) return;
    update(id, (s) => ({ pilot: { ...s.pilot, [agentId]: { ...s.pilot[agentId], ...patch } } }));
}

/// Starts one pilot per selected agent, in order. An agent whose start fails
/// keeps its error; the others still start.
export async function startPilots(id) {
    const session = getSession(id);
    if (!session || session.ended || session.needsReview || !["pilot", "done"].includes(session.cur) || !session.canManage || (session.guard && !session.guard())) return;
    const identity = getAuthIdentityEpoch();
    const succeeded = new Set((session.started || []).filter((r) => r.ok).map((r) => r.agentId));
    const agents = remainingPilotAgents(session).filter((a) => session.pilot[a]?.on && session.pilot[a].endpoints.length);
    if (!agents.length) return;
    update(id, { cur: "starting", activeAgent: null, pilotError: null });
    await flushSessionSave(id).catch(() => {});
    const results = (session.started || []).filter((r) => !agents.includes(r.agentId));
    for (const agentId of agents) {
        if (getSession(id)?.runtimeToken !== session.runtimeToken || (session.guard && !session.guard())) break;
        update(id, { activeAgent: agentId, started: [...results] });
        await flushSessionSave(id).catch(() => {});
        try {
            const result = await session.startPilot(agentId, {
                document: JSON.stringify(documentFor(session, agentId)),
                expectedGeneration: session.today[agentId]?.generation ?? "0",
                endpointIds: session.pilot[agentId].endpoints.map((e) => e.id),
            });
            results.push({ agentId, ok: !!result?.release?.releaseId && !result.rejections?.length,
                releaseId: result?.release?.releaseId || "", unknown: !result?.release?.releaseId && !result?.rejections?.length, endpoints: session.pilot[agentId].endpoints.length,
                warning: result?.investigationSaveError ? "The pilot started, but its investigation response could not be saved. The saved request can recover it." : "",
                rejections: result?.rejections ?? [] });
        } catch (error) {
            results.push({ agentId, ok: false, endpoints: 0, unknown: error?.pilotOutcome !== "not_started", error: String(error?.message || error) });
        }
        if (getSession(id)?.runtimeToken !== session.runtimeToken) break;
        update(id, { started: [...results], activeAgent: null });
        await flushSessionSave(id).catch(() => {});
    }
    if (getSession(id)?.runtimeToken !== session.runtimeToken) return;
    update(id, { cur: "done", started: results, activeAgent: null });
    post(id, "done", session.postCard);
    // Save the final result before leaving the workspace; the debounced save
    // may otherwise lose it when the chat unmounts during navigation.
    await flushSessionSave(id).catch(() => {});
    const first = results.find((r) => r.ok && !succeeded.has(r.agentId));
    if (first && getSession(id) && identity === getAuthIdentityEpoch()) openAgentPolicyPilot(first.agentId, first.releaseId);
}

// ---------------------------------------------------------------- answers

/// A one-line summary of a step's answer, for cards and the model input.
export function summarize(session, step) {
    const v = session.answers[step.id];
    switch (step.card) {
        case "text": return String(v || "");
        case "agents": return AGENT_IDS.filter((a) => inScope(session, a)).map((a) => AGENT_LABELS[a]).join(", ") || "None";
        case "choice": return step.options.find((o) => o.id === v)?.label ?? "No answer";
        case "check": return v ? "Yes" : "No";
        case "features": {
            const off = (step.rows ?? []).filter((r) => v?.[r.id] === "off").map((r) => r.label);
            const open = (step.rows ?? []).filter((r) => !v?.[r.id]).map((r) => r.label);
            return [off.length ? `Off: ${off.join(", ")}` : "All on", open.length ? `Unchanged: ${open.join(", ")}` : ""].filter(Boolean).join(". ");
        }
        case "rules": {
            const rules = (v ?? []).filter((e) => String(e.target ?? "").trim());
            const parts = [...Object.keys(DECISION_LABELS), null].map((d) => {
                const names = rules.filter((e) => (e.decision ?? null) === d).map((e) => e.target);
                return names.length ? `${d ? DECISION_LABELS[d] : "Removed"}: ${names.join(", ")}` : "";
            }).filter(Boolean);
            return parts.length ? parts.join(". ") : "No rules";
        }
        case "themes": {
            const rows = rowsFor(session, step);
            const parts = step.decisions.map((d) => {
                const names = Object.keys(v ?? {}).filter((k) => v[k] === d.id).map((k) => rows.find((r) => r.id === k)?.name ?? k);
                return names.length ? `${d.label}: ${names.join(", ")}` : "";
            }).filter(Boolean);
            return parts.length ? parts.join(". ") : "No decisions";
        }
        case "decisions": {
            const parts = step.decisions.map((d) => {
                const ids = Object.keys(v ?? {}).filter((k) => v[k] === d.id);
                return ids.length ? `${d.label}: ${ids.join(", ")}` : "";
            }).filter(Boolean);
            return parts.length ? parts.join(". ") : "No decisions";
        }
        default: return "";
    }
}

const parseMaybe = (x) => {
    if (typeof x === "string" && /^[[{]/.test(x.trim())) {
        try { return JSON.parse(x); } catch { return x; }
    }
    return x;
};

/// Validates `raw` against the step and returns the answer to store. Throws an
/// Error that names what the step accepts, so the model can correct itself.
export function validateAnswer(session, step, raw) {
    const ans = parseMaybe(raw);
    const current = session.answers[step.id];
    switch (step.card) {
        case "text": {
            if (typeof raw !== "string" || !raw.trim() || raw.trim().length > 4000) throw new Error("Enter between 1 and 4,000 characters of guidance.");
            return raw.trim();
        }
        case "agents": {
            const allowed = AGENT_IDS.filter((a) => (!session.context || session.today[a]) && changeOf(session).support[a].level !== "no");
            const ids = (Array.isArray(ans) ? ans : [ans]).map(String);
            const bad = ids.filter((a) => !allowed.includes(a));
            if (!ids.length || bad.length) throw new Error(`answer must be a non-empty array of: ${allowed.join(", ")}`);
            return Object.fromEntries(allowed.map((a) => [a, ids.includes(a)]));
        }
        case "choice": {
            const option = step.options.find((o) => o.id === String(ans));
            if (!option) throw new Error(`answer must be one of: ${step.options.map((o) => o.id).join(", ")}`);
            return option.id;
        }
        case "check": return ans === true || /^(true|yes)$/i.test(String(ans));
        case "features": {
            if (!ans || typeof ans !== "object" || Array.isArray(ans)) throw new Error('answer must be an object of feature id to "on" or "off"');
            const next = { ...(current ?? {}) };
            for (const [k, v] of Object.entries(ans)) {
                if (!step.rows.some((r) => r.id === k)) throw new Error(`unknown feature ${k}; features: ${step.rows.map((r) => r.id).join(", ")}`);
                if (v !== "on" && v !== "off") throw new Error(`feature ${k}: use "on" or "off"`);
                next[k] = v;
            }
            return next;
        }
        case "decisions": {
            if (!ans || typeof ans !== "object" || Array.isArray(ans)) throw new Error("answer must be an object of row id to a decision id, or to null to clear it");
            const rows = rowsFor(session, step);
            const next = { ...(current ?? {}) };
            for (const [k, v] of Object.entries(ans)) {
                if (!rows.some((r) => r.id === k) && !step.addByName) throw new Error(`unknown row ${k}; rows: ${rows.map((r) => r.id).join(", ")}`);
                if (v === null || v === "" || v === "none") { delete next[k]; continue; }
                if (!step.decisions.some((d) => d.id === v)) throw new Error(`decision for ${k} must be one of: ${step.decisions.map((d) => d.id).join(", ")}, or null`);
                next[k] = v;
            }
            return next;
        }
        case "rules": return mergeRules(current ?? [], ans);
        case "themes": {
            if (!ans || typeof ans !== "object" || Array.isArray(ans)) throw new Error("answer must be an object of group id to a decision id, or to null to clear it");
            const rows = rowsFor(session, step).filter((r) => !r.noRule);
            const next = { ...(current ?? {}) };
            for (const [k, v] of Object.entries(ans)) {
                if (!rows.some((r) => r.id === k)) throw new Error(`unknown group ${k}; groups: ${rows.map((r) => r.id).join(", ")}`);
                if (v === null || v === "" || v === "none") { delete next[k]; continue; }
                if (!step.decisions.some((d) => d.id === v)) throw new Error(`decision for ${k} must be one of: ${step.decisions.map((d) => d.id).join(", ")}, or null`);
                next[k] = v;
            }
            return next;
        }
        default: throw new Error("this step takes no answer");
    }
}

const MAX_TARGET = 200;

/// Merges typed rules into the current ones, by kind and target. A rule with
/// decision null is removed: a rule from today's policy stays listed as
/// removed, so its rules leave the policy; a new one is dropped.
export function mergeRules(current, raw) {
    if (!Array.isArray(raw)) throw new Error('answer must be an array of { kind, target, decision }');
    const next = current.map((e) => ({ ...e }));
    for (const item of raw) {
        const kind = String(item?.kind ?? "");
        const spec = RULE_KINDS[kind];
        if (!spec) throw new Error(`kind must be one of: ${Object.keys(RULE_KINDS).join(", ")}`);
        const target = String(item?.target ?? "").trim();
        if (!target || target.length > MAX_TARGET || /[\r\n]/.test(target)) throw new Error(`target must be 1 to ${MAX_TARGET} characters on one line`);
        if (kind === "mcp" && !/^mcp__\S+$/.test(target)) throw new Error("an MCP tool target is the tool name, such as mcp__server__tool");
        const decision = item?.decision ?? null;
        if (decision !== null && !spec.decisions.includes(decision)) throw new Error(`decision for a ${kind} rule must be one of: ${spec.decisions.join(", ")}, or null`);
        const at = next.findIndex((e) => e.kind === kind && e.target === target);
        const entry = { ...(at >= 0 ? next[at] : { kind, target }), decision, ...(item?.full_paths === true ? { fullPaths: true } : {}) };
        if (at >= 0 && decision === null && !next[at].today) next.splice(at, 1);
        else if (at >= 0) next[at] = entry;
        else if (decision !== null) next.push(entry);
    }
    return next;
}

/// Records an answer from the model, confirms the step, and returns what the
/// model needs to explain it. Throws on an invalid answer.
// Steps whose card collects a list stay open until the person is done.
const LIST_CARDS = new Set(["decisions", "features", "rules", "themes"]);

export function recordAnswer(id, stepId, raw, note, { moveOn } = {}) {
    const session = getSession(id);
    if (session?.ended) throw new Error(ENDED_TEXT);
    if (session?.guard && !session.guard()) throw new Error("Reopen this proposal from its investigation before editing.");
    if (session?.cur === "starting" || session?.started) throw new Error("This proposal was submitted. Start a new reviewed proposal to change it.");
    const step = session && planSteps(session).find((s) => s.id === stepId);
    if (!step) throw new Error(`unknown step; steps: ${planSteps(session).map((s) => s.id).join(", ")}`);
    if (raw !== undefined && raw !== null) draftAnswer(id, stepId, validateAnswer(session, step, raw));
    if (note) addNote(id, stepId, note);
    const isCurrent = getSession(id).cur === stepId;
    if (!isCurrent || moveOn === true || (moveOn !== false && !LIST_CARDS.has(step.card))) confirmStep(id, stepId);
    const after = getSession(id);
    return {
        recorded: summarize(after, step),
        settings: Object.fromEntries(AGENT_IDS.filter((a) => inScope(after, a)).map((a) => [a, changesFor(after, a)])),
        warnings: warnings(after).map((w) => w.text),
    };
}

export const CHANGE_CATALOG = CHANGES;

// Reapply saved choices against freshly loaded policy state.
export async function resumeProposal(id, saved, { progress = false } = {}) {
    if (!saved?.changeId || !chooseChange(id, saved.changeId)) return false;
    let session = getSession(id);
    const on = Object.fromEntries(AGENT_IDS.map((a) => [a, inScope(session, a) && saved.on?.[a] !== false]));
    update(id, { on, done: [], notes: [] });
    for (const step of changeOf(session).steps) {
        if (!Object.hasOwn(saved.answers || {}, step.id)) continue;
        if (step.source) {
            const rows = await session.readSource(step.source);
            if (getSession(id)?.runtimeToken !== session.runtimeToken) throw new Error("The proposal changed while loading its evidence. Reopen it.");
            update(id, (s) => ({ rows: { ...s.rows, [step.source]: rows }, rowsState: { ...s.rowsState, [step.source]: "ready" } }));
        }
        session = getSession(id);
        const answer = validateAnswer(session, step, saved.answers[step.id]);
        update(id, (s) => ({ answers: { ...s.answers, [step.id]: answer } }));
    }
    if (progress) {
        const validSteps = new Set(planSteps(getSession(id)).map((s) => s.id));
        update(id, { done: (saved.done || []).filter((step) => validSteps.has(step)),
            notes: (saved.notes || []).filter((note) => validSteps.has(note.step) && typeof note.text === "string").slice(0, 100).map((note) => ({ step: note.step, text: note.text.slice(0, 300) })),
            pilot: Object.fromEntries(AGENT_IDS.map((agent) => [agent, { on: saved.pilot?.[agent]?.on !== false, endpoints: (saved.pilot?.[agent]?.endpoints || []).slice(0, 50) }])) });
    }
    advance(id);
    return true;
}
